import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  CircularProgress,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DoneIcon from "@mui/icons-material/Done";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import {
  get_notifications,
  mark_notification_read,
  clear_notifications,
  delete_notification,
  delete_all_notifications,
} from "../../../api/ControllApi";
import { useUser } from "../../../context/LoginDetail";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const ensureArray = (data) => {
  if (Array.isArray(data)) return data;
  if (!data) return [];
  if (typeof data === "object") {
    // Check for specific keys in the response
    const items = data.notifications || data.items || data;
    if (Array.isArray(items)) return items;
    // Handle objects with numeric keys
    const values = Object.values(items);
    if (values.length > 0 && (items[Object.keys(items)[0]]?.id || items[0]?.id)) {
      return values;
    }
  }
  return [];
};

const cleanMessage = (msg) => {
  if (!msg) return "";
  return msg
    .replace(/&#8377;/g, "₹")
    .replace(/\u00e2\u201a\u00b9/g, "₹")
    .replace(/&nbsp;/g, " ");
};

const Notifications = () => {
  const { fetchNotificationCount } = useUser();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Confirmation Dialog State
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmType, setConfirmType] = useState("single"); // "single" or "all"
  const [deleteId, setDeleteId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchNotifications = async (pageNo = 1) => {
    setLoading(true);
    try {
      const res = await get_notifications(pageNo);
      if (res.status) {
        setNotifications(ensureArray(res.data));
        setTotalPages(res.data?.pagination?.last_page || 1);
      }
    } catch (err) {
      console.error("Failed to fetch notifications", err);
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications(page);
  }, [page]);

  const handleMarkRead = async (id) => {
    try {
      const res = await mark_notification_read(id);
      if (res.status) {
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
        );
        toast.success("Notification marked as read");
        fetchNotificationCount();
      }
    } catch (err) {
      toast.error("Failed to update notification status");
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const res = await clear_notifications();
      if (res.status) {
        setNotifications([]);
        setPage(1);
        toast.success("All notifications marked as read");
        fetchNotificationCount();
        fetchNotifications();
      }
    } catch (err) {
      toast.error("Failed to clear notifications");
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setConfirmType("single");
    setConfirmOpen(true);
  };

  const handleDeleteAll = () => {
    setConfirmType("all");
    setConfirmOpen(true);
  };

  const executeDelete = async () => {
    setActionLoading(true);
    try {
      if (confirmType === "single") {
        const res = await delete_notification(deleteId);
        if (res.status) {
          setNotifications((prev) => prev.filter((n) => n.id !== deleteId));
          toast.success("Notification deleted successfully");
          fetchNotificationCount();
          if (notifications.length === 1 && page > 1) {
            setPage(page - 1);
          } else {
            fetchNotifications(page);
          }
        }
      } else {
        const res = await delete_all_notifications();
        if (res.status) {
          setNotifications([]);
          setPage(1);
          toast.success("All notifications deleted successfully");
          fetchNotificationCount();
        }
      }
    } catch (err) {
      toast.error(`Failed to delete ${confirmType === "single" ? "notification" : "all notifications"}`);
    } finally {
      setActionLoading(false);
      setConfirmOpen(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 0 } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h5" fontWeight={600} color="primary.dark">
          Notifications
        </Typography>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DoneAllIcon />}
            onClick={handleMarkAllRead}
            disabled={loading || notifications.length === 0}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Mark all read
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteSweepIcon />}
            onClick={handleDeleteAll}
            disabled={loading || notifications.length === 0}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Delete all
          </Button>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid #edf2f7",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
            <CircularProgress size={32} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 8, textAlign: "center" }}>
            <img
              src="/assets/images/nodatafound.png"
              alt="No notifications"
              style={{ width: 120, opacity: 0.6, marginBottom: 16 }}
            />
            <Typography variant="body1" color="text.secondary">
              You have no notifications at the moment.
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  alignItems="flex-start"
                  sx={{
                    px: { xs: 2, md: 3 },
                    py: 2,
                    bgcolor: notification.is_read ? "transparent" : "#95b1600a",
                    transition: "background-color 0.2s",
                    "&:hover": { bgcolor: "#f7fafc" },
                  }}
                  secondaryAction={
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      {!notification.is_read && (
                        <Tooltip title="Mark as read">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleMarkRead(notification.id)}
                            sx={{ color: "primary.main" }}
                          >
                            {/* <DoneIcon fontSize="small" /> */}
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleDelete(notification.id)}
                          sx={{ color: "error.main" }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: notification.is_read ? 500 : 700,
                            color: notification.is_read ? "text.primary" : "primary.dark",
                          }}
                        >
                          {notification.title}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1, lineHeight: 1.5 }}
                        >
                          {cleanMessage(notification.message)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.disabled", display: "block" }}>
                          {dayjs(notification.created_at).fromNow()} •{" "}
                          {dayjs(notification.created_at).format("DD MMM YYYY, hh:mm A")}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {!loading && notifications.length > 0 && totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => !actionLoading && setConfirmOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            maxWidth: "400px",
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pt: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: 56,
              height: 56,
              borderRadius: "50%",
              bgcolor: "error.light",
              color: "error.main",
              mx: "auto",
              mb: 2,
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 32 }} />
          </Box>
          <Typography variant="h6" fontWeight={700}>
            {confirmType === "single" ? "Delete Notification" : "Delete All Notifications"}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", pb: 0 }}>
          <Typography color="text.secondary">
            {confirmType === "single"
              ? "Are you sure you want to delete this notification? This action cannot be undone."
              : "Are you sure you want to delete all notifications? This action will permanently remove all your alerts."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 2, p: 3 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={actionLoading}
            variant="outlined"
            sx={{
              borderRadius: "50px",
              px: 3,
              textTransform: "none",
              color: "text.secondary",
              borderColor: "divider",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={executeDelete}
            disabled={actionLoading}
            variant="contained"
            color="error"
            sx={{
              borderRadius: "50px",
              px: 3,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {actionLoading ? "Deleting..." : "Delete Now"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Notifications;
